import { useTranslation } from 'react-i18next';

import { Modal } from '@hotjar/hotsauce';
import { DisconnectionState } from '@hotjar/webapp/domains/sso/hooks/useIdpDisconnectionFlow';
import type { UseIdpDisconnectionFlow } from '@hotjar/webapp/domains/sso/hooks/useIdpDisconnectionFlow';

import { DisconnectionSuccessfulModal } from './modals/DisconnectionSuccessfulModal';
import { ConfirmDisconnectionModal } from './modals/ConfirmDisconnectionModal';

interface Props {
  idpDisconnectionFlow: UseIdpDisconnectionFlow;
}

export const DisconnectOrganizationModals = ({
  idpDisconnectionFlow,
}: Props): JSX.Element | null => {
  const { t } = useTranslation();

  const {
    isLoading,
    disconnectionState,
    selectedOrganization,
    dismissDisconnection,
    confirmDisconnection,
  } = idpDisconnectionFlow;

  if (!selectedOrganization?.id) {
    return null;
  }

  const { name: organizationName } = selectedOrganization;

  let modalTitle: string;

  switch (disconnectionState) {
    case DisconnectionState.SUCCESS:
      modalTitle = t('common:settings.sso.disconnect-modal.content.confirm-title', {
        name: organizationName,
      });
      break;
    case DisconnectionState.CONFIRM:
    default:
      modalTitle = t('common:settings.sso.disconnect-modal.title');
      break;
  }

  return (
    <Modal
      isOpen={
        disconnectionState === DisconnectionState.CONFIRM ||
        disconnectionState === DisconnectionState.SUCCESS
      }
      contentLabel={modalTitle}
      onClose={dismissDisconnection}
      size="m"
    >
      {disconnectionState === DisconnectionState.CONFIRM && (
        <ConfirmDisconnectionModal
          isLoading={isLoading}
          organization={selectedOrganization}
          onDismiss={dismissDisconnection}
          onDisconnect={confirmDisconnection}
        />
      )}

      {disconnectionState === DisconnectionState.SUCCESS && (
        <DisconnectionSuccessfulModal
          organization={selectedOrganization}
          onConfirmSubmit={dismissDisconnection}
        />
      )}
    </Modal>
  );
};
